import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import MaskedTextField from '@/common/components/MaskedTextField'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import TextField from '@/common/components/TextField'
import { fiscalDocumentKindOptions } from '@/common/constants/fiscalDocumentKindOptions.constant'
import { numericRegexMask } from '@/common/constants/masks.constant'
import { options as unitForQuantityOptions } from '@/common/constants/unitForQuantityOptions'
import { useEquipmentCategories } from '@/common/hooks/queries/useEquipmentCategories'
import { useProductCategories } from '@/common/hooks/queries/useProductCategories'
import useDebounce from '@/common/hooks/useDebounce'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { Mapper } from '@/common/services/mapper'
import { ProductCategoryData } from '@/common/types/productCategory.type'
import { FormTransformers } from '@/common/utils/formTransformers'
import { EquipmentCategoryData } from '@/features/equipmentModels/types/equipmentCategories'
import { tefProviderOptions, tefProviders } from '@/features/establishments/constants/tefModels.constant'
import { useUpdateProduct } from '@/features/products/hooks/useUpdateProduct'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronsRight, PlusIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import useProduct from '../../hooks/useProduct'
import { ProductValidationSchema, createSchema } from '../../schemas/create.schema'

const Update = () => {
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const { id } = useParams()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchProductCategory, setSearchProductCategory] = useDebounce('')
  const [searchEquipmentCategory, setSearchEquipmentCategory] = useDebounce('')

  const { data: initialProduct, isLoading: initialProductIsLoading } = useProduct(id || '')

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasUpdateProductPermission = !isUserPermissionsPending ? hasUserPermission('Admin::Products::Update') : true

  const { data: productCategoriesOptions, isPending: productCategoriesOptionsIsPending } = useProductCategories({
    filters: { search: searchProductCategory },
    mapper: Mapper.mapToOptions<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    reset,
    formState: { errors },
  } = useForm<ProductValidationSchema>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      category: { label: initialProduct?.category?.display_name, value: initialProduct?.category.id },
      display_name: initialProduct?.display_name,
      unit_for_quantity: unitForQuantityOptions.find((option) => option.value === initialProduct?.unit_for_quantity),
      product_tef_identifiers: initialProduct?.product_tef_identifiers?.length
        ? initialProduct?.product_tef_identifiers?.map((tefIdentifier) => ({
          identifier_id: tefIdentifier.id,
          identifier: tefIdentifier.identifier,
        }))
        : [{ identifier: '' }],
      equipment_category_ids:
        (initialProduct?.equipment_categories?.length &&
          initialProduct?.equipment_categories.map((category) => ({
            label: category.display_name,
            value: category.id,
          }))) ||
        undefined,
      fiscal_document_kind: fiscalDocumentKindOptions.find(
        (option) => option.value === initialProduct?.fiscal_document_kind,
      ),
    },
  })

  const { mutate: updateProduct, isPending: updateProductIsPending } = useUpdateProduct({
    productId: id || '',
    product: initialProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', id] })
      goBack({ fallback: `/products/${id}` })
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const {
    fields: productTefIdentifierFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'product_tef_identifiers',
  })

  const watchProductTefIdentifiers = watch('product_tef_identifiers')

  const onSubmit: SubmitHandler<ProductValidationSchema> = (data) => updateProduct(data)

  const { data: equipmentCategoriesOptions } = useEquipmentCategories({
    filters: { search: searchEquipmentCategory },
    mapper: Mapper.mapToOptions<EquipmentCategoryData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  })

  useEffect(() => {
    if (initialProductIsLoading) return

    reset({
      category: { label: initialProduct?.category?.display_name, value: initialProduct?.category.id },
      display_name: initialProduct?.display_name,
      unit_for_quantity: unitForQuantityOptions.find((option) => option.value === initialProduct?.unit_for_quantity),
      product_tef_identifiers: initialProduct?.product_tef_identifiers?.length
        ? initialProduct?.product_tef_identifiers?.map((tefIdentifier) => ({
          identifier_id: tefIdentifier.id,
          tef_provider: tefProviderOptions?.find((provider) => provider.value === tefIdentifier.tef_provider) || {
            label: '',
            value: null,
          },
          identifier: tefIdentifier.identifier,
        }))
        : [{ identifier: '' }],
      equipment_category_ids:
        (initialProduct?.equipment_categories?.length &&
          initialProduct?.equipment_categories.map((category) => ({
            label: category.display_name,
            value: category.id,
          }))) ||
        undefined,
      fiscal_document_kind: fiscalDocumentKindOptions.find(
        (option) => option.value === initialProduct?.fiscal_document_kind,
      ),
    })
  }, [initialProductIsLoading])

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasUpdateProductPermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/products/${id}`} />

            <p className="text-white-400 font-normal">Produtos</p>

            <ChevronsRight className="text-white-400" />

            <h2>{initialProduct?.display_name}</h2>
          </div>

          <div className="flex gap-4">
            <Button variant="outline-error" onClick={() => goBack({ fallback: `/products/${id}` })}>
              Cancelar
            </Button>

            <Button
              data-testid="update_products"
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={updateProductIsPending}
            >
              Confirmar
            </Button>
          </div>
        </div>

        {(initialProductIsLoading || isUserPermissionsPending) ? (
          <Box>Carregando...</Box>
        ) : (
          <Box className="flex max-h-[calc(100vh-128px)] flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto">
            <p className="text-lg font-bold">Editar produto</p>

            <div>
              <SelectController
                control={control}
                name="category"
                label="Categoria"
                dataTestid="category_selector"
                isLoading={productCategoriesOptionsIsPending}
                onInputChange={setSearchProductCategory}
                defaultValue={{ label: initialProduct?.category.display_name, value: initialProduct?.category?.id }}
                options={productCategoriesOptions}
                errorMessage={errors?.category?.message || undefined}
                isDisabled={updateProductIsPending}
              />
            </div>

            <div className="flex gap-2">
              <TextField
                label="Nome"
                data-testid="display_name_input"
                errorMessage={errors?.display_name?.message || undefined}
                className="w-1/2"
                defaultValue={initialProduct?.display_name}
                {...register('display_name')}
                disabled={updateProductIsPending}
              />

              <div className="w-1/2">
                <SelectController
                  control={control}
                  name="unit_for_quantity"
                  dataTestid="unit_for_quantity_selector"
                  label="Métrica"
                  defaultValue={unitForQuantityOptions.find(
                    (option) => option.value === initialProduct?.unit_for_quantity,
                  )}
                  options={unitForQuantityOptions}
                  errorMessage={errors?.unit_for_quantity?.message || undefined}
                  isDisabled={updateProductIsPending}
                />
              </div>
            </div>

            <div className="flex w-full flex-col gap-4">
              <p className="text-xs -mb-3">Identificadores</p>

              {productTefIdentifierFields.map((field, index) => {
                const tefProvider = watchProductTefIdentifiers?.[index]?.tef_provider?.value

                return (
                  <div key={field.id} className="flex gap-2 items-start">
                    <SelectController
                      control={control}
                      name={`product_tef_identifiers.${index}.tef_provider`}
                      label="Provedor TEF"
                      dataTestid={`product_tef_identifiers_${index}_tef_provider_selector`}
                      options={tefProviderOptions}
                      triggerOnChange={() => {
                        setValue(`product_tef_identifiers.${index}.identifier`, '')
                      }}
                      className="w-full"
                      isClearable
                      errorMessage={errors.product_tef_identifiers?.[index]?.tef_provider?.message || undefined}
                      isDisabled={updateProductIsPending}
                    />

                    {!!tefProvider ? (
                      <Controller
                        control={control}
                        name={`product_tef_identifiers.${index}.identifier`}
                        render={({ field }) => (
                          <MaskedTextField
                            {...field}
                            dataTestid={`product_tef_identifiers_${index}_identifier_input`}
                            label={`Identificador ${tefProvider ? tefProviders[tefProvider]?.label : ''}`}
                            mask={
                              tefProvider
                                ? tefProviders[tefProvider].regexMaskFn(tefProviders[tefProvider].codeLength)
                                : numericRegexMask(6)
                            }
                            prepareChar={(char) => char.toUpperCase()}
                            className="w-full"
                            errorMessage={errors.product_tef_identifiers?.[index]?.identifier?.message || undefined}
                            disabled={updateProductIsPending}
                          />
                        )}
                      />
                    ) : (
                      <TextField label="Identificador" placeholder="Preencha o Provedor" className="w-full" disabled />
                    )}

                    {index === 0 ? (
                      <Button
                        type="button"
                        variant="outline-error"
                        onClick={() => append({ tef_provider: { value: null, label: '' }, identifier: '' })}
                        disabled={updateProductIsPending}
                        className="size-[3.25rem] flex items-center justify-center mt-5"
                      >
                        <PlusIcon className="text-blue-800" />
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline-error"
                        onClick={() => remove(index)}
                        disabled={updateProductIsPending}
                        className="size-[3.25rem] flex items-center justify-center mt-5"
                      >
                        <Trash2Icon className="text-danger" />
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>

            {!!watchProductTefIdentifiers?.length &&
              watchProductTefIdentifiers?.some((productTefIdentifier) => !!productTefIdentifier?.identifier) ? (
              <SelectController
                control={control}
                name="equipment_category_ids"
                dataTestid="equipment_category_ids_selector"
                label="Categorias de equipamento"
                onInputChange={setSearchEquipmentCategory}
                options={equipmentCategoriesOptions || []}
                isMulti
                errorMessage={errors?.equipment_category_ids?.message || undefined}
                isDisabled={updateProductIsPending}
              />
            ) : (
              <TextField
                label="Categorias de equipamento"
                placeholder="Preencha ao menos um Identificador para selecionar"
                disabled
              />
            )}

            <TextField
              label="Porte"
              disabled
              value={initialProduct?.equipment_tier?.display_name || ''}
            />

            <SelectController
              control={control}
              name="fiscal_document_kind"
              dataTestid="fiscal_document_kind_selector"
              label="Tipo de Documento Fiscal"
              errorMessage={errors?.fiscal_document_kind?.message}
              options={fiscalDocumentKindOptions}
              isDisabled={updateProductIsPending}
            />
          </Box>
        )}

        <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
      </form>
    </ErrorMessageWrapper>
  )
}

export default Update
